import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DashboardFilters = ({ filters, onFilterChange }) => {
  const [expanded, setExpanded] = useState(false);

  const currentYear = new Date()?.getFullYear();
  const currentMonth = new Date()?.getMonth() + 1;

  const periodOptions = [
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'quarter', label: 'Trimestre' },
    { value: 'year', label: 'Año' },
    { value: 'custom', label: 'Personalizado' },
  ];

  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  }));

  const monthOptions = [
    { value: '01', label: 'Enero' }, { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' }, { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' }, { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' }, { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' }, { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' }, { value: '12', label: 'Diciembre' },
  ];

  const quarterOptions = [
    { value: 'Q1', label: 'T1 (Ene-Mar)' },
    { value: 'Q2', label: 'T2 (Abr-Jun)' },
    { value: 'Q3', label: 'T3 (Jul-Sep)' },
    { value: 'Q4', label: 'T4 (Oct-Dic)' },
  ];

  const getActivePeriodLabel = () => {
    const pt = filters?.periodType;
    const names = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    if (pt === 'day' && filters?.singleDay) return filters?.singleDay;
    if (pt === 'week' && filters?.week) return `Semana ${filters?.week}`;
    if (pt === 'month' && filters?.month) {
      const [y, m] = filters?.month?.split('-');
      return `${names?.[parseInt(m) - 1]} ${y}`;
    }
    if (pt === 'quarter' && filters?.quarter) return `${filters?.quarter} ${filters?.year || currentYear}`;
    if (pt === 'year' && filters?.year) return `Año ${filters?.year}`;
    if (pt === 'custom' && filters?.dateFrom && filters?.dateTo) return `${filters?.dateFrom} → ${filters?.dateTo}`;
    return 'Sin filtro';
  };

  const handleReset = () => {
    onFilterChange({
      periodType: 'month',
      dateFrom: '', dateTo: '', singleDay: '', week: '', quarter: '',
      year: String(currentYear),
      month: `${currentYear}-${String(currentMonth)?.padStart(2, '0')}`,
    });
  };

  const selectClass = "bg-[#111111] border border-white/15 text-white/80 text-xs font-oranienbaum rounded-lg px-3 py-2 focus:outline-none focus:border-[#D6EF4E]/50 focus:ring-1 focus:ring-[#D6EF4E]/30 cursor-pointer appearance-none min-w-[100px]";
  const inputClass = "bg-[#111111] border border-white/15 text-white/80 text-xs font-oranienbaum rounded-lg px-3 py-2 focus:outline-none focus:border-[#D6EF4E]/50 focus:ring-1 focus:ring-[#D6EF4E]/30 min-w-[130px]";

  const currentMonthVal = filters?.month?.split('-')?.[1] || String(currentMonth)?.padStart(2, '0');

  return (
    <div className="bg-[#1A1A1A] rounded-xl border border-white/8 shadow-sm">
      {/* Main compact row */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <Icon name="SlidersHorizontal" size={14} className="text-[#D6EF4E] flex-shrink-0" />

        {/* Period type */}
        <select
          value={filters?.periodType}
          onChange={(e) => onFilterChange({ ...filters, periodType: e?.target?.value })}
          className={selectClass}
        >
          {periodOptions?.map((o) => (
            <option key={o?.value} value={o?.value}>{o?.label}</option>
          ))}
        </select>

        {/* Year — shown for year/quarter/month */}
        {(filters?.periodType === 'year' || filters?.periodType === 'quarter' || filters?.periodType === 'month') && (
          <select
            value={filters?.year || String(currentYear)}
            onChange={(e) => {
              const updated = { ...filters, year: e?.target?.value };
              if (filters?.periodType === 'month' && filters?.month) {
                const [, m] = filters?.month?.split('-');
                updated.month = `${e?.target?.value}-${m}`;
              }
              onFilterChange(updated);
            }}
            className={selectClass}
          >
            {yearOptions?.map((o) => (
              <option key={o?.value} value={o?.value}>{o?.label}</option>
            ))}
          </select>
        )}

        {/* Month selector */}
        {filters?.periodType === 'month' && (
          <select
            value={currentMonthVal}
            onChange={(e) => onFilterChange({ ...filters, month: `${filters?.year || currentYear}-${e?.target?.value}` })}
            className={selectClass}
          >
            {monthOptions?.map((o) => (
              <option key={o?.value} value={o?.value}>{o?.label}</option>
            ))}
          </select>
        )}

        {/* Quarter selector */}
        {filters?.periodType === 'quarter' && (
          <select
            value={filters?.quarter || 'Q1'}
            onChange={(e) => onFilterChange({ ...filters, quarter: e?.target?.value })}
            className={selectClass}
          >
            {quarterOptions?.map((o) => (
              <option key={o?.value} value={o?.value}>{o?.label}</option>
            ))}
          </select>
        )}

        {/* Day selector */}
        {filters?.periodType === 'day' && (
          <input
            type="date"
            value={filters?.singleDay || ''}
            onChange={(e) => onFilterChange({ ...filters, singleDay: e?.target?.value })}
            className={inputClass}
          />
        )}

        {/* Custom range */}
        {filters?.periodType === 'custom' && (
          <>
            <input
              type="date"
              value={filters?.dateFrom || ''}
              onChange={(e) => onFilterChange({ ...filters, dateFrom: e?.target?.value })}
              className={inputClass}
              placeholder="Desde"
            />
            <span className="text-white/30 text-xs">→</span>
            <input
              type="date"
              value={filters?.dateTo || ''}
              onChange={(e) => onFilterChange({ ...filters, dateTo: e?.target?.value })}
              className={inputClass}
              placeholder="Hasta"
            />
          </>
        )}

        {/* Active label badge */}
        <span className="font-oranienbaum text-white/40 bg-white/5 px-2 py-1 rounded-full border border-white/8 whitespace-nowrap text-xs">
          {getActivePeriodLabel()}
        </span>

        <div className="flex items-center gap-2 ml-auto">
          {/* Advanced options toggle */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs font-oranienbaum text-white/40 hover:text-[#D6EF4E] transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10"
          >
            <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={12} />
            <span className="hidden sm:inline text-base">{expanded ? 'Menos' : 'Avanzado'}</span>
          </button>

          {/* Reset */}
          <button
            onClick={handleReset}
            className="flex items-center gap-1 font-oranienbaum text-white/40 hover:text-red-400 transition-colors px-2 py-1.5 rounded-lg hover:bg-red-500/5 border border-transparent hover:border-red-500/20 text-[15px]"
          >
            <Icon name="RotateCcw" size={12} />
            <span className="hidden sm:inline">Limpiar</span>
          </button>
        </div>
      </div>

      {/* Advanced options panel */}
      {expanded && (
        <div className="border-t border-white/8 px-4 py-3">
          <p className="text-[10px] font-oranienbaum text-white/30 uppercase tracking-widest mb-2">Accesos rápidos</p>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Hoy', action: () => { const t = new Date()?.toISOString()?.split('T')?.[0]; onFilterChange({ ...filters, periodType: 'day', singleDay: t }); } },
              { label: 'Este mes', action: () => { const m = String(currentMonth)?.padStart(2,'0'); onFilterChange({ ...filters, periodType: 'month', month: `${currentYear}-${m}` }); } },
              { label: 'T1', action: () => onFilterChange({ ...filters, periodType: 'quarter', quarter: 'Q1', year: String(currentYear) }) },
              { label: 'T2', action: () => onFilterChange({ ...filters, periodType: 'quarter', quarter: 'Q2', year: String(currentYear) }) },
              { label: 'T3', action: () => onFilterChange({ ...filters, periodType: 'quarter', quarter: 'Q3', year: String(currentYear) }) },
              { label: 'T4', action: () => onFilterChange({ ...filters, periodType: 'quarter', quarter: 'Q4', year: String(currentYear) }) },
              { label: 'Este año', action: () => onFilterChange({ ...filters, periodType: 'year', year: String(currentYear) }) },
              { label: 'Año anterior', action: () => onFilterChange({ ...filters, periodType: 'year', year: String(currentYear - 1) }) },
            ]?.map((p) => (
              <button
                key={p?.label}
                onClick={p?.action}
                className="px-3 py-1.5 text-xs font-oranienbaum rounded-lg border border-white/10 bg-white/3 text-white/60 hover:bg-[#D6EF4E]/10 hover:border-[#D6EF4E]/30 hover:text-[#D6EF4E] transition-colors"
              >
                {p?.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardFilters;